import * as d3 from 'd3'
import * as React from 'react'

import { isFirefoxDesktop } from '../shared/isMobile'
import { BubbleSettings } from './constants/settings'
import Draggable from './draggable'
import { BubbleNode, Range } from './types'
import { getSizeScale } from './utils'

interface SliderProps {
  bubbles: BubbleNode[]
  type: string
  height: number
  width: number
  scale: any
  range: Range
  format?: string
  onRangeChange: (range: Range) => void
  settings: BubbleSettings
}

interface SliderState {
  leftHandle: number
  rightHandle: number
  leftHandleDragging: boolean
  leftHandleHovered: boolean
  rightHandleDragging: boolean
  rightHandleHovered: boolean
  tooltipShown: boolean
  counts: number[]
  opacity: (count: number) => number
  max: number
}

class Slider extends React.Component<SliderProps, SliderState> {
  // @ts-ignore
  canvasContext: CanvasRenderingContext2D

  canvas = React.createRef<HTMLCanvasElement>()

  constructor(props: SliderProps) {
    super(props)
    var bubbles = props.bubbles
    var size = props.type === 'vertical' ? props.height : props.width
    var counts: number[] = []
    for (var i = 0; i < size; i++) {
      counts[i] = 0
    }

    var rScale = getSizeScale(bubbles)
    bubbles.forEach((d) => {
      var val = (props.type === 'vertical' ? d.y : d.x) ?? 0
      var x = Math.round(props.scale(val))
      var r = Math.max(Math.round(rScale(d.size)), 1.5)
      counts[x]++
      for (let i = 1; i < r; i++) {
        if (x - i >= 0) {
          counts[x - i] += 1
        }
      }
      for (let i = 1; i < r && x + i < size; i++) {
        counts[x + i] += 1
      }
    })

    var min = 0,
      max = Math.max.apply(
        null,
        counts.filter((d) => d >= 0)
      ),
      opacity = d3.scaleLinear().range([2, 100]).domain([min, max])

    var leftHandle = 0,
      rightHandle = props.type === 'vertical' ? props.height : props.width
    if (props.range) {
      leftHandle = Math.max(leftHandle, props.scale.copy().clamp(true)(props.range.min))
      rightHandle = Math.min(rightHandle, props.scale.copy().clamp(true)(props.range.max))
      if (leftHandle > rightHandle) {
        var temp = leftHandle
        leftHandle = rightHandle
        rightHandle = temp
      }
    }

    this.state = {
      leftHandle,
      rightHandle,
      leftHandleHovered: false,
      leftHandleDragging: false,
      rightHandleHovered: false,
      rightHandleDragging: false,
      tooltipShown: false,
      counts,
      opacity,
      max,
    }
  }

  render() {
    var width = this.props.width,
      height = 9,
      style = {
        width: 9,
        height: 17,
        marginTop: 3,
        marginLeft: -5,
      }
    if (this.props.type === 'vertical') {
      width = 9
      height = this.props.height
      style.width = 17
      style.height = 9
      style.marginTop = -3
      style.marginLeft = -4
    }

    return (
      <div style={{ position: 'relative' }} className="slider">
        <canvas width={width} height={height} ref={this.canvas} />
        <Draggable
          type={this.props.type}
          style={style}
          initialPos={
            this.props.type === 'vertical' ? { y: this.state.leftHandle, x: 0 } : { x: this.state.leftHandle, y: 0 }
          }
          onChange={this._onHandleChange.bind(this, 'left')}
          onChanged={this._onHandleChanged.bind(this, 'left')}
          onMouseEnter={this._showTooltip.bind(this, 'left')}
          onMouseLeave={this._hideTooltip.bind(this, 'left')}
          tooltipShown={
            this.state.leftHandleHovered ||
            this.state.leftHandleDragging ||
            this.state.rightHandleHovered ||
            this.state.rightHandleDragging
          }
          tooltipValue={this.props.scale.invert(this.state.leftHandle)}
          tooltipFormat={this.props.format}
        />
        <Draggable
          style={style}
          type={this.props.type}
          initialPos={
            this.props.type === 'vertical' ? { y: this.state.rightHandle, x: 0 } : { x: this.state.rightHandle, y: 0 }
          }
          onChange={this._onHandleChange.bind(this, 'right')}
          onChanged={this._onHandleChanged.bind(this, 'right')}
          onMouseEnter={this._showTooltip.bind(this, 'right')}
          onMouseLeave={this._hideTooltip.bind(this, 'right')}
          tooltipShown={
            this.state.leftHandleHovered ||
            this.state.leftHandleDragging ||
            this.state.rightHandleHovered ||
            this.state.rightHandleDragging
          }
          tooltipValue={this.props.scale.invert(this.state.rightHandle)}
          tooltipFormat={this.props.format}
        />
      </div>
    )
  }

  componentDidMount() {
    const canvas = this.canvas.current
    const context = canvas?.getContext('2d', isFirefoxDesktop() ? { willReadFrequently: true } : undefined)

    if (!canvas || !context) return

    this.canvasContext = context
    this._renderCanvas()
  }

  componentDidUpdate() {
    this._renderCanvas()
  }

  _renderCanvas = () => {
    var { counts, opacity, max } = this.state
    const settings = this.props.settings.slider
    var size = this.props.type === 'vertical' ? this.props.height : this.props.width
    var context = this.canvasContext

    var width = this.props.width,
      height = 9
    if (this.props.type === 'vertical') {
      width = 9
      height = this.props.height
    }

    context.save()
    context.clearRect(0, 0, width, height)

    if (this.props.type === 'vertical') {
      context.translate(width, 0)
      context.rotate(Math.PI / 2)
    }

    context.strokeStyle = settings.stroke
    context.beginPath()
    context.moveTo(0, 4.5)
    context.lineTo(size, 4.5)
    context.stroke()

    if (this.state.leftHandle !== 0 || this.state.rightHandle !== size) {
      context.strokeStyle = settings.highlightStroke
      context.beginPath()
      context.moveTo(this.state.leftHandle + 1, 4.5)
      context.lineTo(this.state.rightHandle, 4.5)
      context.stroke()
    }

    for (var i = 0; i < size; i++) {
      var count = counts[i]
      if (count === 0) {
        continue
      }
      if (
        i >= this.state.leftHandle &&
        i <= this.state.rightHandle &&
        (this.state.leftHandle !== 0 || this.state.rightHandle !== size)
      ) {
        context.fillStyle = settings.distributionHighligt(opacity(count) / 100)
      } else {
        context.fillStyle = settings.distribution(opacity(count) / 100)
      }
      context.fillRect(i, count > max * 0.6 ? 3 : 4, 1, count > max * 0.6 ? 3 : 1)
    }

    if (this.state.leftHandle !== 0 || this.state.rightHandle !== size) {
      context.fillStyle = settings.highlight
      context.fillRect(this.state.leftHandle + 1, 0, this.state.rightHandle - this.state.leftHandle, 9)
    }

    context.restore()
  }

  _onHandleChange = (type: string, value: number) => {
    var size = this.props.type === 'vertical' ? this.props.height : this.props.width
    if (value < 0) {
      value = 0
    } else if (value > size) {
      value = size
    }

    if (type === 'left' && value >= this.state.rightHandle) {
      value = this.state.rightHandle - 1
    } else if (type === 'right' && value <= this.state.leftHandle) {
      value = this.state.leftHandle + 1
    }

    var state: Record<string, any> = {}
    state[type + 'Handle'] = value
    state[type + 'HandleDragging'] = true
    // @ts-ignore
    this.setState(state)

    return value
  }

  _onHandleChanged = (type: string, value: number) => {
    var state: Record<string, any> = {}
    state[type + 'Handle'] = value
    state[type + 'HandleDragging'] = false
    // @ts-ignore
    this.setState(state)

    // @ts-ignore
    var range: Range = {
      min: this.state.leftHandle,
      max: this.state.rightHandle,
    }
    if (type === 'left') {
      range.min = value
    } else {
      range.max = value
    }

    range.scaled = {
      min: this.props.scale.invert(range.min),
      max: this.props.scale.invert(range.max),
    }

    this.props.onRangeChange(range)
  }

  _showTooltip = (type: string) => {
    var state: Record<string, any> = {}
    state[type + 'HandleHovered'] = true
    // @ts-ignore
    this.setState(state)
  }

  _hideTooltip = (type: string) => {
    var state: Record<string, any> = {}
    state[type + 'HandleHovered'] = false
    // @ts-ignore
    this.setState(state)
  }
}

export default Slider
